
import Vue from 'vue'
import DiscountAppliedTo from '@/Models/Static/DiscountAppliedTo'
import PaymentMethod from '@/Models/Static/PaymentMethod'
import TableType from '@/Models/Static/TableType'

const HANDY_INTEGRATION = 70 // constant as defined in RevoXef’s App\Models\Modules\Integration

const state = () => ({
  /**
   * This variable contains all the non-catalog tables in JSON format.
   * @type Object
   */
  data: {},
})

const getters = {
  _get: state => (model, id) => state.data[model]?.find(o => o.id == id),

  integration: state => state.data.Integration?.find(o => !! o.active && o.type == HANDY_INTEGRATION),

  rooms: state => [...state.data.TableRoom ?? []].filter(o => !! o.active).sortByNumber('order'),
  tables: state => room_id => state.data.TableTable
    ?.filter(table => TableType.isTable(table.type_id) && table.room_id == room_id)
    .sortByNatural('name')
    ?? [],
  tablePrices: state => [...state.data.TablePrice ?? []].filter(o => !! o.active),
  tablePrice: (state, getters, rootState) => getters['tablePrices']
    .find(o => o.id == rootState.settings.values.tablePriceId)
    ?? getters['tablePrices'][0],

  // Payment Methods
  paymentMethod: (state, getters) => id => getters._get('PaymentMethod', id),
  availablePaymentMethods: (state, getters) => {
    const methods = getters.integration
      ?.config
      ?.payment_methods
      ?.map(id => getters.paymentMethod(id))
      ?? state.data.PaymentMethod?.filter(o => !! o.active)
      ?? []

    return methods
      .filter(o => ! PaymentMethod.isDisabled(o))
      .sortByNumber('order')
  },
  cashPayment: (state, getters) => getters.availablePaymentMethods.find(o => PaymentMethod.isCash(o.id)),
  cardPayment: (state, getters) => getters.availablePaymentMethods.find(o => PaymentMethod.isCard(o.id)),
  otherPaymentMethods: (state, getters, rootState, rootGetters) => getters.availablePaymentMethods
    ?.filter(o => PaymentMethod.isOther(o.id) && (o.id !== PaymentMethod.VOID || rootGetters['user/can']('voidPayments')))
    ?? [],
  endTillPaymentMethods: (state, getters) => getters.availablePaymentMethods
    ?.filter(o => !! o.requiresEndTill)
    ?? [],

  tax: (state, getters) => id => getters._get('Tax', id),
  business: state => prop => {
    const business = state.data.Business?.[0]
    return business && prop ? business[prop] : business
  },
  ticket: state => prop => {
    return state.data.PrintTicket?.length
      ? JSON.parse(state.data.PrintTicket[0][prop])
      : {}
  },

  orderDiscounts: state => state.data.Discount
    ?.filter(o => !! o.active && (o.applyTo == DiscountAppliedTo.ORDER || o.applyTo == DiscountAppliedTo.BOTH))
    .sortByNumber('order')
    ?? [],
  itemDiscounts: state => state.data.Discount
    ?.filter(o => !! o.active && (o.applyTo == DiscountAppliedTo.ITEM || o.applyTo == DiscountAppliedTo.BOTH))
    .sortByNumber('order')
    ?? [],

  waiters: state => state.data.TenantUser
    ?.filter(o => !! o.active)
    ?? [],
  waiter: state => id => {
    const waiter = state.data.TenantUser?.find(o => id == o.id)
    return waiter ? String(`${waiter.name ?? ''} ${waiter.surname ?? ''}`).trim() : ''
  },

  cashiers: state => [...state.data.Cashier ?? []].sortByString('name'),
  cashier: (state, getters, rootState) => {
    if (! state.data.Cashier?.length) {
      AndroidDevice.warn('No cashiers')
      return
    }
    const id = rootState.user.data.cashier_id || rootState.settings.values.cashier
    return state.data.Cashier.find(o => o.id == id) ?? state.data.Cashier[0]
  },
  hasCashMachine: (state, getters) => !! getters['cashier']?.cash_machine_id,

  kitchenNotes: state => [...state.data.KitchenNote ?? []].sortByNumber('order'),
}

const mutations = {
  set (state, { model, data }) {
    Vue.set(state.data, model, data)
  },
}

const actions = {}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
